import { useMemo, useState } from 'react';
import { AlertCircle, Eye, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useConfirm } from '@/components/ui/confirm';
import { formatBytes, formatModifiedAt } from '@/features/tfx/lib/display';
import { useMediaPackages, useSetPackageListed, type MediaPackage } from '../api';
import { CurationStatusBadge, PackageSourceBadge } from './MediaBadges';
import { PackagePreviewDialog } from './PackagePreviewDialog';

type StatusFilter = 'ALL' | 'ACTIVE' | 'DELISTED';

/**
 * Shared catalogue table for both effects and transitions — they are one .tfx
 * catalog split by caps, so the only difference is which slice each page shows.
 *
 * <p>UNKNOWN packages (whose document could not be read, so caps are unknown)
 * are shown on the EFFECT page rather than hidden: filtering them out of both
 * pages would leave them live on every device with no way to take them down.
 */
export function MediaPackageCatalog({
  kind,
  title,
  emptyMessage,
}: {
  kind: 'EFFECT' | 'TRANSITION';
  title: string;
  emptyMessage: string;
}) {
  const { data, isLoading, isFetching, isError, error, refetch } = useMediaPackages();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [previewId, setPreviewId] = useState<string | null>(null);

  const rows = useMemo(() => {
    const all = data ?? [];
    const mine = all.filter((p) =>
      kind === 'TRANSITION' ? p.kind === 'TRANSITION' : p.kind !== 'TRANSITION',
    );
    const needle = search.trim().toLowerCase();
    return mine.filter((p) => {
      if (status !== 'ALL' && p.status !== status) return false;
      if (!needle) return true;
      return (
        p.packageId.toLowerCase().includes(needle) ||
        (p.name ?? '').toLowerCase().includes(needle) ||
        (p.category ?? '').toLowerCase().includes(needle)
      );
    });
  }, [data, kind, search, status]);

  const activeCount = rows.filter((p) => p.status === 'ACTIVE').length;

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        <span className="text-sm text-muted-foreground">
          {activeCount} live / {rows.length} shown
        </span>
        <div className="flex-1" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search id, name or category"
          className="h-9 w-64"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          aria-label="Filter by status"
        >
          <option value="ALL">All statuses</option>
          <option value="ACTIVE">Live only</option>
          <option value="DELISTED">Delisted only</option>
        </select>
        <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching}>
          <RefreshCw className={isFetching ? 'size-4 animate-spin' : 'size-4'} />
          Reload
        </Button>
      </div>

      {isError && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <div className="flex items-center gap-2">
            <AlertCircle className="size-4 shrink-0" />
            <span>{error instanceof Error ? error.message : 'Failed to load the catalog.'}</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      )}

      {isLoading && rows.length === 0 ? (
        <div className="flex flex-1 items-center justify-center py-24">
          <Loader2 className="size-6 animate-spin text-primary" />
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Package</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Modified</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-16 text-center text-muted-foreground">
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((pkg) => (
                  <PackageRow
                    key={pkg.packageId}
                    pkg={pkg}
                    onPreview={() => setPreviewId(pkg.packageId)}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <PackagePreviewDialog packageId={previewId} onClose={() => setPreviewId(null)} />
    </div>
  );
}

function PackageRow({ pkg, onPreview }: { pkg: MediaPackage; onPreview: () => void }) {
  const confirm = useConfirm();
  const mutation = useSetPackageListed();
  const isActive = pkg.status === 'ACTIVE';

  const onToggle = async () => {
    const delisting = isActive;
    const { confirmed } = await confirm({
      title: delisting ? 'Delist package' : 'Relist package',
      description: delisting
        ? [
            `Remove "${pkg.packageId}" from the mobile catalog?`,
            'Every app flavour — including Release — reads this catalog, so it stops being offered to production users within ~5 minutes.',
            'Existing projects that use it are not damaged: it renders as a no-op and stays in the draft.',
            pkg.factoryManaged
              ? 'This package is factory-managed: a later `tfx_publish.py publish` run rebuilds it from local files and would restore it.'
              : '',
          ]
            .filter(Boolean)
            .join(' ')
        : `Return "${pkg.packageId}" to the mobile catalog?`,
      confirmText: delisting ? 'Delist' : 'Relist',
      variant: delisting ? 'destructive' : 'default',
    });
    if (!confirmed) return;
    try {
      await mutation.mutateAsync({ packageId: pkg.packageId, listed: !delisting });
      toast.success(delisting ? 'Package delisted' : 'Package relisted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update the package');
    }
  };

  return (
    <TableRow>
      <TableCell className="font-mono text-xs">
        {pkg.packageId}
        <span className="text-muted-foreground">@{pkg.version}</span>
        {pkg.kind === 'UNKNOWN' && (
          <span
            className="ml-2 text-amber-500"
            title="The package document could not be read, so its caps are unknown"
          >
            unclassified
          </span>
        )}
      </TableCell>
      <TableCell>{pkg.name?.trim() || '—'}</TableCell>
      <TableCell className="text-muted-foreground">{pkg.category?.trim() || '—'}</TableCell>
      <TableCell>
        <PackageSourceBadge source={pkg.source} />
      </TableCell>
      <TableCell>
        <CurationStatusBadge status={pkg.status} />
      </TableCell>
      <TableCell>{formatBytes(pkg.bytes)}</TableCell>
      <TableCell>{formatModifiedAt(pkg.modifiedAt)}</TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onPreview}
            disabled={pkg.goldenFrames === 0}
            title={pkg.goldenFrames === 0 ? 'This package published no preview frames' : 'Preview'}
          >
            <Eye className="size-4" />
          </Button>
          <Button
            variant={isActive ? 'destructive' : 'outline'}
            size="sm"
            disabled={mutation.isPending}
            onClick={() => void onToggle()}
          >
            {isActive ? 'Delist' : 'Relist'}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
